import React from "react";
import { useNavigate } from "react-router";
import { IoInformationCircleOutline, IoWarningOutline } from "react-icons/io5";
import {
  getBillingInvoiceNumber,
  getTimesheetModeLabel,
  isLegacySplitShift,
  isV5MultiAssignmentTimesheet,
} from "../TabSection/NewCodeAllocation/v5BillingUtils";

const modeStyles = {
  Advance: "bg-indigo-50 border-indigo-200 text-indigo-900",
  "Split shift": "bg-amber-50 border-amber-200 text-amber-900",
  Standard: "bg-slate-50 border-slate-200 text-slate-700",
};

export const CodeAllocationInvoiceLockAlert = ({ invoiceNumber, compact = false }) => {
  const navigate = useNavigate();
  const invoiceLabel = invoiceNumber ? (
    <>
      invoice <span className="font-bold">{invoiceNumber}</span> has
    </>
  ) : (
    "an invoice has"
  );

  return (
    <div
      role="alert"
      className={
        compact
          ? "flex items-start gap-1.5 text-red-800"
          : "flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-red-800"
      }
    >
      <IoWarningOutline
        className={`shrink-0 text-red-500 ${compact ? "mt-0.5 h-4 w-4" : "mt-0.5 h-5 w-5"}`}
        aria-hidden
      />
      <p className={compact ? "text-[11px] font-semibold leading-4" : "text-xs font-semibold leading-5"}>
        You cannot change this code allocation because {invoiceLabel} been
        created against it. Please void the invoice first.{" "}
        <span
          onClick={() => navigate("/rostering/invoicing")}
          className="cursor-pointer font-bold text-blue-600 underline hover:text-blue-800"
        >
          Go to invoices
        </span>
      </p>
    </div>
  );
};

/**
 * Context banner for Timesheet Details (worker pay lane) and Code Allocation (billing lane).
 * Split-shift time-lock warnings are intentionally not shown — timesheet fields stay editable.
 */
const TimesheetModeBanner = ({ shiftDetails, lane = "details" }) => {
  if (!shiftDetails) return null;

  const mode = getTimesheetModeLabel(shiftDetails);
  const isV5 = isV5MultiAssignmentTimesheet(shiftDetails);
  const legacySplit = isLegacySplitShift(shiftDetails);
  const style = modeStyles[mode] || modeStyles.Standard;

  // Details lane: keep V5 worker-pay note only.
  if (lane === "details") {
    if (!isV5) return null;
    return (
      <div className="flex flex-col gap-2 mb-3">
        <div
          className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${modeStyles.Advance}`}
        >
          <span className="text-[10px] font-bold uppercase tracking-wide opacity-70">
            Advance timesheet
          </span>
          <span className="text-xs font-medium">
            Worker pay — hours, KM, and notes apply to this worker only.
          </span>
        </div>
      </div>
    );
  }

  // Billing lane: invoice lock alert is shown with the Update Allocation button.
  // Legacy split message is rendered by Code Allocation (`ca-split-warning`).
  if (legacySplit) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 mb-3">
      <div
        className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${style}`}
      >
        <span className="text-[10px] font-bold uppercase tracking-wide opacity-70">
          {mode} timesheet
        </span>
        {isV5 && (
          <span className="text-xs font-medium flex items-center gap-1">
            <IoInformationCircleOutline className="shrink-0 h-4 w-4" aria-hidden />
            Billing applies to the whole shift; editing from another worker&apos;s timesheet
            shows the same lines.
          </span>
        )}
      </div>
    </div>
  );
};

export default TimesheetModeBanner;
